import Link from "../Link";
import NextLink from "next/link";
import { BlogSEO } from "../SEO";
import siteMetadata from "@/data/siteMetadata";
import ScrollTopAndComment from "../ScrollTopAndComment";
import {
  chakra,
  Box,
  Heading,
  Text,
  Flex,
  Spacer,
  Grid,
  Image as ChakraImage,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useToken,
} from "@chakra-ui/react";
import { Fragment, useEffect, useMemo } from "react";
import OptimizedImage from "../../shared/OptimizedImage";
import { LinkButton } from "../../shared/generic";
import ProfilePic from "../../profile/ProfilePic";
import { find } from "@/utils/data/colleges-data";
import { blogAnalytics } from "@/utils/analytics";
import { useFeatureFlag } from "@/contexts/FeatureFlagsContext";
import { ScrollBackUpTriggeredModal } from "../../shared/ScrollTriggeredModal";
import { DATABASE_SIZE } from "@/utils/profile";
import fancyHeaderFont from "@/utils/style/gloock-font";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { alea } from "seedrandom";
import CollegeAdvisorForm from "@/components/collegeadvisor/CollegeAdvisorForm";
import CollegeAdvisorModal from "@/components/collegeadvisor/CollegeAdvisorModal";

const postDateTemplate = { year: "numeric", day: "numeric", month: "short" };
const CTA_PROFILES = "control";
const CTA_INTERNSHIPS = "internships";
// const CTA_INTERNSHIP_TEMPLATE = "internship_template"
// const CTA_SCHOLARSHIPS        = "scholarships"
const CTA_ANANTH = "ananth";
const CTA_ESSAYS_THAT_WORKED = "essays_that_worked";

const CTA_DATA = {
  [CTA_PROFILES]: {
    header: "Find your twin at your dream school, and see how they got in.",
    text: `We have a database of ${DATABASE_SIZE.applications} exemplary college applications from around the country, complete with essays, extracurriculars, and awards. Get inspiration to craft your own application.`,
    button: "Find my twin",
    image: "/images/leadgen/profiles.png",
    href: "/profiles",
  },
  [CTA_ESSAYS_THAT_WORKED]: {
    header: "Read essays that worked at Harvard, Stanford, and MIT.",
    text: "We're releasing a few exemplary essays that got students into top schools. Check them out while they're available for free!",
    button: "Read essays",
    image: "/images/leadgen/profiles.png",
    href: "/example-essays/harvard",
  },
  [CTA_ANANTH]: {
    header: "A Stanford admit just shared their college apps with the world...",
    text: "Ananth wasn't a legacy student, and his dad didn't donate a building. But he was an expert at crafting a compelling narrative, and he won over Stanford admissions officers. Want to know how he did it? Read through his 10 essays, SAT/ACT scores, extracurricular activities, and awards, for free.",
    button: "Read for free",
    image: "/images/leadgen/profiles.png",
    href: "/profile/obeNZeeu28aj8RjZNQ7PNTPrdkC2",
  },
  [CTA_INTERNSHIPS]: {
    header: "Find a prestigious summer program or internship",
    text: "We've curated the world's largest database of summer opportunities for you, and made them searchable by your grade & interests. The best part? It's free to use.",
    href: "/summer-programs-and-internships",
    image: "/images/leadgen/internships.png",
    button: "Search through thousands",
  },
  internship_templates: {
    header: "Internship Email Templates",
    text: "Wanna land a prestigious summer internship? Check out our tried-and-true templates for reaching out to professors, alumni, and recruiters.",
    href: "/tools/free-internship-email-template",
    image: "/images/leadgen/internships.png",
    button: "Read templates",
  },
};

const RelatedProfile = ({ index, id, display_name, university, colleges }) => {
  const shadowColors = ["red", "orange", "blue"];
  const colorLight = useToken("colors", shadowColors[index % shadowColors.length] + ".200");
  return (
    <NextLink href={`/profile/${id}`} legacyBehavior>
      <Flex cursor="pointer" align="center" gap={4} mt={1} bg="gray.100" borderRadius="full">
        <ProfilePic uid={id} boxSize={20} shadow={`-0.2rem 0rem ${colorLight}`} />
        <Box flex={1} py={1}>
          <Text fontWeight="bold" fontSize="lg" noOfLines={1}>
            {display_name}
          </Text>
          <Text fontSize="sm">
            {find(university).name} (+{colleges.length} colleges)
          </Text>
        </Box>
        <ChevronRightIcon mr={4} boxSize={4} />
      </Flex>
    </NextLink>
  );
};

const ModalCTA = () => {
  const id = useFeatureFlag("blog_cta_modal_id", "control");
  const { header, image, text, button, href } = CTA_DATA[id];

  const onChangeShown = (shown) => {
    if (shown) {
      blogAnalytics.onShowModal(id);
    } else {
      blogAnalytics.onCloseModal(id);
    }
  };

  useEffect(() => {
    // Preload the image so it's ready when the modal opens
    if (image) {
      const img = new Image();
      img.src = image;
    }
  }, [image]);

  return (
    <ScrollBackUpTriggeredModal
      scrollPos={{ base: 700, md: 1000 }}
      delay={1000}
      onChangeShown={onChangeShown}
    >
      <ModalHeader px={8} py={8} fontSize="3xl" fontFamily="heading" textAlign="center">
        {header}
      </ModalHeader>
      <ModalBody textAlign="center">
        <Text fontSize="lg" textAlign="center">
          {text}
        </Text>
        {image && <ChakraImage display="inline" mt={4} w="70%" src={image} />}
      </ModalBody>
      <ModalFooter>
        <Flex>
          <Spacer />
          <LinkButton
            onClick={() => blogAnalytics.onClickModal(id)}
            href={href}
            colorScheme="green"
            rightIcon={<ChevronRightIcon />}
            size="lg"
          >
            {button}
          </LinkButton>
        </Flex>
      </ModalFooter>
    </ScrollBackUpTriggeredModal>
  );
};

const RelatedArticle = (post) => (
  <NextLink href={`/blog/${post.slug}`} key={post.slug} legacyBehavior>
    <Flex cursor="pointer" mt={4}>
      <Box flex={4} bg="gray.100" align="stretch" p={3} gap={2} borderLeftRadius="md">
        <Text flex={3} noOfLines={2} fontSize="md" fontWeight="bold" lineHeight={1.2}>
          {post.title}
        </Text>
        <Text noOfLines={2} mt={2} lineHeight={1.2} fontSize="sm">
          {post.summary}
        </Text>
      </Box>
      <Box flex={1} pos="relative">
        <OptimizedImage
          layout="fill"
          objectFit="cover"
          src={post.img}
          alt={post.title}
          borderRightRadius="md"
        />
      </Box>
    </Flex>
  </NextLink>
);

const SidebarCTA = ({ slug, prev, next, previewProfiles }) => {
  const indices = Array(previewProfiles.length).keys();

  const profiles = useMemo(() => {
    const rng = alea(slug);
    const shuffled = [...indices].sort(() => rng() - 0.5);
    return shuffled.slice(0, 3).map((i) => previewProfiles[i]);
  });

  return (
    <Box pos="sticky" mt={{ base: 0, md: 8 }} top={8} height="min-content">
      <Heading as="h3" fontSize="2xl">
        Read applications
      </Heading>
      <Text my={4}>
        Read the essays, activities, and awards that got them in.{" "}
        <Link href="/plans">Read one for free</Link>!
      </Text>
      {profiles.map((profile, i) => (
        <RelatedProfile {...profile} index={i} key={i} />
      ))}
      <Heading as="h3" fontSize="2xl" mt={8}>
        Related articles
      </Heading>
      {[prev, next]
        .filter((i) => i)
        .map((post) => (
          <RelatedArticle {...post} key={post.slug} />
        ))}
    </Box>
  );
};

export default function PostLayout({
  frontMatter,
  authorDetails,
  next,
  prev,
  children,
  previewProfiles,
}) {
  const { slug, fileName, date, lastmod, title, img, tags } = frontMatter;
  let ctaSidebar = frontMatter.sidebar || CTA_PROFILES;
  let ctaFooter = frontMatter.footer || CTA_ANANTH;

  return (
    <>
      <BlogSEO
        url={`${siteMetadata.siteUrl}/blog/${slug}`}
        authorDetails={authorDetails}
        {...frontMatter}
      />
      <ScrollTopAndComment />
      <CollegeAdvisorModal />
      <article>
        <Grid
          mt={0}
          mb={4}
          gap={4}
          templateColumns={{ base: "1fr", md: "3fr 1fr" }}
          alignItems="top"
        >
          <Box pr={{ base: 0, md: 16 }}>
            <chakra.h1
              className={fancyHeaderFont.className}
              mt={4}
              lineHeight="1.2"
              fontSize={{ base: "3xl", md: "5xl" }}
            >
              {title}
            </chakra.h1>
            <Flex my={4} alignItems="center" gap={2} listStyleType="none">
              {authorDetails.map((author) => (
                <Fragment key={author.name}>
                  {author.avatar && (
                    <OptimizedImage
                      priority
                      src={author.avatar}
                      width={60}
                      height={60}
                      borderRadius="full"
                      alt="avatar"
                    />
                  )}
                  <Box>
                    <Text fontFamily="heading" fontWeight="bold">
                      {author.name}
                    </Text>
                    <Flex
                      color="gray.600"
                      direction={{ base: "column", md: "row" }}
                      align={{ md: "center" }}
                      gap={{ md: 1.5 }}
                    >
                      <Text>{author.company}</Text>
                      <Text display={{ base: "none", md: "inline" }}>&middot;</Text>
                      <chakra.time dateTime={lastmod ?? date}>
                        {lastmod && "Updated "}
                        {new Date(lastmod ?? date).toLocaleDateString(
                          siteMetadata.locale,
                          postDateTemplate
                        )}
                      </chakra.time>
                      <Text display={{ base: "none", md: "inline" }}>&middot;</Text>
                      <Text>{frontMatter.readingTime.text}</Text>
                    </Flex>
                  </Box>
                </Fragment>
              ))}
            </Flex>
            <Box w="100%" sx={{ aspectRatio: "16 / 9" }} pos="relative">
              {img && (
                <OptimizedImage
                  priority
                  objectFit="cover"
                  layout="fill"
                  src={img}
                  alt={title}
                  borderRadius="md"
                />
              )}
            </Box>
            <Box
              fontSize="xl"
              mt={10}
              maxW="95vw"
              sx={{
                "p, h1, h2, h3, h4, h5, h6, ul, ol": {
                  mt: "1em",
                },
                h1: {
                  fontSize: "2.25rem",
                },
                h2: {
                  fontSize: "1.875rem",
                },
                h3: {
                  fontSize: "1.5rem",
                },
                "h1, h2, h3": {
                  fontFamily: "heading",
                },
                "ol, ul": {
                  pl: "1.5em",
                },
              }}
            >
              {children}
            </Box>
          </Box>
          <SidebarCTA slug={slug} prev={prev} next={next} previewProfiles={previewProfiles} />
        </Grid>
      </article>
    </>
  );
}
